/**
 * TerraMatrix Universal Data Platform - VariableForm Component
 *
 * Dynamic form component for rendering variable inputs based on
 * VariableDefinition types from query templates.
 *
 * Phase 5: Data Page UI Integration
 */

import React from 'react';
import type { VariableDefinition } from '@/services/api/queryTemplateApi';

// Time preset options for timePreset type variables
const TIME_PRESET_OPTIONS = [
  { value: 'last_hour', label: 'Last Hour' },
  { value: 'last_24h', label: 'Last 24 Hours' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7d', label: 'Last 7 Days' },
  { value: 'last_30d', label: 'Last 30 Days' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

interface VariableFormProps {
  /** Variable definitions from the template */
  variables: Record<string, VariableDefinition>;
  /** Current variable values */
  values: Record<string, unknown>;
  /** Callback when values change */
  onChange: (values: Record<string, unknown>) => void;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Optional class name */
  className?: string;
}

export const VariableForm: React.FC<VariableFormProps> = ({
  variables,
  values,
  onChange,
  disabled = false,
  className = '',
}) => {
  // Filter out system variables (they're auto-injected)
  const editableVariables = Object.entries(variables).filter(
    ([, def]) => def.type !== 'system'
  );

  if (editableVariables.length === 0) {
    return (
      <div className={`text-sm text-muted-foreground ${className}`}>
        This template has no configurable variables.
      </div>
    );
  }

  const handleChange = (key: string, value: unknown) => {
    onChange({
      ...values,
      [key]: value,
    });
  };

  const renderInput = (key: string, definition: VariableDefinition) => {
    const currentValue = values[key] ?? definition.default;

    switch (definition.type) {
      case 'timePreset':
        return (
          <select
            value={currentValue as string || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background
                       focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select time range...</option>
            {TIME_PRESET_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );

      case 'enum':
        return (
          <select
            value={currentValue as string || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            disabled={disabled}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background
                       focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select...</option>
            {definition.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt.charAt(0).toUpperCase() + opt.slice(1)}
              </option>
            ))}
          </select>
        );

      case 'number':
        return (
          <input
            type="number"
            value={currentValue as number || ''}
            onChange={(e) => handleChange(key, e.target.value ? Number(e.target.value) : undefined)}
            disabled={disabled}
            placeholder={`Enter ${key}...`}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background
                       focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                       disabled:opacity-50 disabled:cursor-not-allowed"
          />
        );

      case 'dateRange':
        return (
          <div className="flex gap-2">
            <input
              type="date"
              value={(currentValue as { from?: string })?.from || ''}
              onChange={(e) => handleChange(key, { ...(currentValue as object), from: e.target.value })}
              disabled={disabled}
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background
                         focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                         disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="self-center text-muted-foreground">to</span>
            <input
              type="date"
              value={(currentValue as { to?: string })?.to || ''}
              onChange={(e) => handleChange(key, { ...(currentValue as object), to: e.target.value })}
              disabled={disabled}
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background
                         focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                         disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        );

      case 'string':
      default:
        return (
          <input
            type="text"
            value={currentValue as string || ''}
            onChange={(e) => handleChange(key, e.target.value)}
            disabled={disabled}
            placeholder={`Enter ${key}...`}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background
                       focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                       disabled:opacity-50 disabled:cursor-not-allowed"
          />
        );
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {editableVariables.map(([key, definition]) => (
        <div key={key}>
          <label className="block text-sm font-medium mb-1.5">
            {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
            {definition.required && <span className="text-destructive ml-1">*</span>}
          </label>
          {definition.description && (
            <p className="text-xs text-muted-foreground mb-2">{definition.description}</p>
          )}
          {renderInput(key, definition)}
        </div>
      ))}
    </div>
  );
};

export default VariableForm;
